/**
 * Hedita Enterprise - Configurazione Feature React Query Hooks
 * Guest App Config, iCal Sync, Avvisi
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { GuestAppConfig, GuestAppConfigInsert, GuestAppConfigUpdate } from './types';
import type { ICalSync, ICalSyncInsert, ICalSyncUpdate } from './types';
import type { Avviso, AvvisoInsert, AvvisoUpdate } from './types';
import {
  getGuestAppConfig,
  createGuestAppConfig,
  updateGuestAppConfig,
  upsertGuestAppConfig,
  getICalSyncs,
  getICalSyncById,
  createICalSync,
  updateICalSync,
  deleteICalSync,
  triggerICalSync,
  getAvvisi,
  getAvvisoById,
  getAvvisiAttivi,
  createAvviso,
  updateAvviso,
  deleteAvviso,
  markAvvisoAsRead,
} from '@/api/queries/configurazione';

// ============================================================
// GUEST APP CONFIG HOOKS
// ============================================================

const GUEST_CONFIG_KEY = 'guest-app-config';

export function useGuestAppConfig(adminId?: string) {
  return useQuery({
    queryKey: [GUEST_CONFIG_KEY, adminId],
    queryFn: async () => {
      if (!adminId) return null;
      const { data, error } = await getGuestAppConfig(adminId);
      if (error) throw error;
      return data;
    },
    enabled: !!adminId,
  });
}

export function useCreateGuestAppConfig() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ config, adminId }: { config: GuestAppConfigInsert; adminId: string }) => {
      const { data, error } = await createGuestAppConfig(config, adminId);
      if (error) throw error;
      return data as GuestAppConfig;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [GUEST_CONFIG_KEY, variables.adminId] });
    },
  });
}

export function useUpdateGuestAppConfig() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates, adminId }: { id: string; updates: GuestAppConfigUpdate; adminId: string }) => {
      const { data, error } = await updateGuestAppConfig(id, updates, adminId);
      if (error) throw error;
      return data as GuestAppConfig;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [GUEST_CONFIG_KEY, variables.adminId] });
    },
  });
}

export function useUpsertGuestAppConfig() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ config, adminId }: { config: GuestAppConfigInsert; adminId: string }) => {
      const { data, error } = await upsertGuestAppConfig(config, adminId);
      if (error) throw error;
      return data as GuestAppConfig;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [GUEST_CONFIG_KEY, variables.adminId] });
    },
  });
}

// ============================================================
// ICAL SYNC HOOKS
// ============================================================

const ICAL_SYNC_KEY = 'ical-sync';

export function useICalSyncs(adminId?: string) {
  return useQuery({
    queryKey: [ICAL_SYNC_KEY, adminId],
    queryFn: async () => {
      if (!adminId) return [];
      const { data, error } = await getICalSyncs({ userId: adminId });
      if (error) throw error;
      return (data || []) as ICalSync[];
    },
    enabled: !!adminId,
  });
}

export function useICalSync(id?: string, adminId?: string) {
  return useQuery({
    queryKey: [ICAL_SYNC_KEY, id, adminId],
    queryFn: async () => {
      if (!id || !adminId) return null;
      const { data, error } = await getICalSyncById(id, adminId);
      if (error) throw error;
      return data as ICalSync | null;
    },
    enabled: !!id && !!adminId,
  });
}

export function useCreateICalSync() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sync, adminId }: { sync: ICalSyncInsert; adminId: string }) => {
      const { data, error } = await createICalSync(sync, adminId);
      if (error) throw error;
      return data as ICalSync;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [ICAL_SYNC_KEY, variables.adminId] });
    },
  });
}

export function useUpdateICalSync() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates, adminId }: { id: string; updates: ICalSyncUpdate; adminId: string }) => {
      const { data, error } = await updateICalSync(id, updates, adminId);
      if (error) throw error;
      return data as ICalSync;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [ICAL_SYNC_KEY, variables.adminId] });
    },
  });
}

export function useDeleteICalSync() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, adminId }: { id: string; adminId: string }) => {
      const { error } = await deleteICalSync(id, adminId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [ICAL_SYNC_KEY, variables.adminId] });
    },
  });
}

export function useTriggerICalSync() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, adminId }: { id: string; adminId: string }) => {
      const { data, error } = await triggerICalSync(id, adminId);
      if (error) throw error;
      return data as ICalSync;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [ICAL_SYNC_KEY, variables.adminId] });
    },
  });
}

// ============================================================
// AVVISI HOOKS
// ============================================================

const AVVISI_KEY = 'avvisi';

export function useAvvisi(adminId?: string) {
  return useQuery({
    queryKey: [AVVISI_KEY, adminId],
    queryFn: async () => {
      if (!adminId) return [];
      const { data, error } = await getAvvisi({ userId: adminId });
      if (error) throw error;
      return (data || []) as Avviso[];
    },
    enabled: !!adminId,
  });
}

export function useAvvisiAttivi(adminId?: string) {
  return useQuery({
    queryKey: [AVVISI_KEY, 'attivi', adminId],
    queryFn: async () => {
      if (!adminId) return [];
      const { data, error } = await getAvvisiAttivi(adminId);
      if (error) throw error;
      return (data || []) as Avviso[];
    },
    enabled: !!adminId,
  });
}

export function useAvviso(id?: string, adminId?: string) {
  return useQuery({
    queryKey: [AVVISI_KEY, id, adminId],
    queryFn: async () => {
      if (!id || !adminId) return null;
      const { data, error } = await getAvvisoById(id, adminId);
      if (error) throw error;
      return data as Avviso | null;
    },
    enabled: !!id && !!adminId,
  });
}

export function useCreateAvviso() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ avviso, adminId }: { avviso: AvvisoInsert; adminId: string }) => {
      const { data, error } = await createAvviso(avviso, adminId);
      if (error) throw error;
      return data as Avviso;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [AVVISI_KEY, variables.adminId] });
    },
  });
}

export function useUpdateAvviso() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates, adminId }: { id: string; updates: AvvisoUpdate; adminId: string }) => {
      const { data, error } = await updateAvviso(id, updates, adminId);
      if (error) throw error;
      return data as Avviso;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [AVVISI_KEY, variables.adminId] });
    },
  });
}

export function useDeleteAvviso() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, adminId }: { id: string; adminId: string }) => {
      const { error } = await deleteAvviso(id, adminId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [AVVISI_KEY, variables.adminId] });
    },
  });
}

export function useMarkAvvisoAsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, adminId }: { id: string; adminId: string }) => {
      const { data, error } = await markAvvisoAsRead(id, adminId);
      if (error) throw error;
      return data as Avviso;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [AVVISI_KEY, variables.adminId] });
    },
  });
}
